/**
 * Links opt in with `#detour_open_app=true`. It is a fragment because the
 * browser never sends a fragment to the server, so the flag stays out of the
 * click, its params and the link the app gets. Facebook drops the fragment,
 * but needs no flag: on Android it tries the app on every visit (see
 * appIntent.ts), and on iOS it leaves through a page on another host (see
 * lib/inAppEscape/appBounce.ts).
 */
const OPEN_APP_FLAG = "detour_open_app";

const ESCAPED_HASH = "%23";

function hasOpenAppFlag(fragment: string): boolean {
  return new URLSearchParams(fragment).get(OPEN_APP_FLAG) === "true";
}

/**
 * Returns the link without its fragment, so the flag never reaches the app or
 * starts another attempt. Null for a page that is not http(s).
 */
export function parseLinkUrl(pageUrl: string): URL | null {
  let url: URL;
  try {
    url = new URL(pageUrl);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  url.hash = "";
  return url;
}

/** Like parseLinkUrl, but null when the page has no flag. */
export function parseFlaggedLinkUrl(pageUrl: string): URL | null {
  const hashIndex = pageUrl.indexOf("#");
  const fragment = hashIndex === -1 ? "" : pageUrl.slice(hashIndex + 1);
  if (!hasOpenAppFlag(fragment)) return null;

  return parseLinkUrl(pageUrl);
}

/**
 * Workaround for Instagram on iOS. It saves a bio link with "#" turned into
 * "%23", and sometimes "=" into "%3D", so the flag reaches the server inside
 * the path or the query. Instagram also adds its own utm_* params after it.
 *
 * Returns the URL the link should have been: params back in the query, flag
 * back in the fragment. Returns null for other URLs, including ones where the
 * "%23" is the customer's own, like `?tag=%23sale`.
 */
export function restoreEscapedOpenAppFlag(requestUrl: string): string | null {
  const { origin, pathname, search } = new URL(requestUrl);
  const pathAndQuery = pathname + search;

  const escapedHashIndex = pathAndQuery.lastIndexOf(ESCAPED_HASH);
  if (escapedHashIndex === -1) return null;

  // With "%23" in the path, the link had no query of its own, so any query here
  // was added by Instagram.
  const isInPath = escapedHashIndex < pathname.length;
  const afterEscapedHash = (isInPath ? pathname : pathAndQuery).slice(
    escapedHashIndex + ESCAPED_HASH.length
  );

  let escapedParams: URLSearchParams;
  try {
    // Next rewrites the query and encodes "=" as "%3D" on the way.
    escapedParams = new URLSearchParams(decodeURIComponent(afterEscapedHash));
  } catch {
    return null;
  }
  if (escapedParams.get(OPEN_APP_FLAG) !== "true") return null;
  escapedParams.delete(OPEN_APP_FLAG);

  const query = [
    isInPath
      ? search.slice(1)
      : pathAndQuery.slice(pathname.length + 1, escapedHashIndex),
    // Usually Instagram's utm_* params.
    escapedParams.toString(),
  ]
    .filter(Boolean)
    .join("&");

  return (
    `${origin}${pathname.slice(0, escapedHashIndex)}` +
    `${query ? `?${query}` : ""}#${OPEN_APP_FLAG}=true`
  );
}
